import { Edit } from '@mui/icons-material';
import { Grid2, Paper } from '@mui/material';
import { Stack } from '@mui/system';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { BillingPermissions } from '@thro-types/access-handler';
import { Customer } from '@thro-types/billing';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { validate as uuidValidate } from 'uuid';

import { useCustomerImportMutation, useGetCustomersListQuery } from 'api/hooks/billing/customer';
import { CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { UploadButton } from 'app/components/UploadButton';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { countries, countryToFlag } from 'utils/Countries';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { CustomerDrawerActions } from '../CustomerDrawer/customerDrawerSlice';
import './CustomerOverview.scss';

export const CustomerOverview = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  const { permissions } = useAuthContext();

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'companyName', sort: 'asc' }],
    defaultPageSize: 100
  });

  const [isUploading, setIsUploading] = useState(false);

  usePageHeading({
    heading: t(translations.pages.billingCustomer.heading())
  });

  const { isLoading, data } = useGetCustomersListQuery(true, {
    params: {
      skip: page * pageSize,
      take: pageSize,
      search: debouncedSearchText,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort
    }
  });

  const { create } = useCustomerImportMutation();

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsUploading(true);
      if (!event.target.files) return;
      const formData = new FormData();
      formData.append('file', event.target.files[0]);
      await create.mutateAsync(formData);
      dispatch(AlertActions.success(t(translations.pages.billingCustomer.alerts.uploadSuccess())));
      setIsUploading(false);
    } catch (error) {
      dispatch(AlertActions.error(t(translations.common.alerts.failedFileUpload())));
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams) => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto'
                // color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            dispatch(CustomerDrawerActions.setCustomer(params.row));
            dispatch(CustomerDrawerActions.open());
          }}
          disabled={isUploading}
          // showInMenu
        />
      ];
    },
    [t, dispatch, isUploading]
  );

  const columns = useMemo((): TypedGridColumns<Customer> => {
    return [
      {
        field: 'customerId',
        flex: 1,
        headerName: t(translations.common.id()),
        cellClassName: 'overflow-hidden-ellipsis',
        sortable: false,
        renderCell: ({ row }) => `${uuidValidate(row.customerId) ? '' : row.customerId}`
      },
      {
        field: 'firstName',
        flex: 2,
        headerName: t(translations.common.contactPerson()),
        renderCell: ({ row }) => `${row.firstName || ''} ${row.lastName || ''}`
      },
      {
        field: 'companyName',
        flex: 2,
        headerName: t(translations.common.companyName())
      },
      {
        field: 'email',
        flex: 2,
        headerName: t(translations.common.email())
      },
      {
        field: 'phone',
        flex: 2,
        sortable: false,
        headerName: t(translations.common.phone())
      },
      {
        field: 'country',
        flex: 2,
        headerName: t(translations.common.country()),
        renderCell: ({ row }) => (
          <>
            <span style={{ marginRight: '0.5em' }}>
              {countryToFlag(countries.find(c => c.code === row.country)?.code || '')}
            </span>
            {row.country}
          </>
        )
      },
      {
        field: 'ustId',
        flex: 2,
        sortable: false,
        headerName: t(translations.common.taxId_short())
      },
      {
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <>
      <Stack gap={1}>
        <Grid2 container gap={1} justifyContent={'space-between'} alignItems={'center'}>
          <Searchfield handleSearch={handleSearchTextChange} search={search} />
          <Grid2 container gap={1} alignItems={'center'}>
            {/* {isUploading && <CircularProgress size={24} color="primary" />} */}
            {permissions.includes(BillingPermissions.BILLING_FINANCE_WRITE) && (
              <UploadButton isLoading={isUploading} onChange={handleUpload}>
                {t(translations.pages.billingCustomer.import())}
              </UploadButton>
            )}
            {permissions.includes(BillingPermissions.BILLING_CUSTOMER_WRITE) && (
              <CreateButton
                renderSkeleton={isLoading}
                onClick={() => dispatch(CustomerDrawerActions.open())}
              />
            )}
          </Grid2>
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <Grid2 component={Paper} style={{ height: '80vh' }} container>
            <DataGrid
              columns={columns}
              rows={data?.data || []}
              rowCount={data?.count || 0}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={handlePaginationChange}
              loading={isLoading}
              onRowClick={event => {
                navigate(`/billing/customers/${event.row.customerId}`, {
                  state: { back: currentURL }
                });
              }}
              sortingMode="server"
              sortModel={sortingModel}
              onSortModelChange={handleSortModelChange}
              disableColumnMenu
              disableColumnFilter
              disableRowSelectionOnClick
              columnVisibilityModel={{
                actions: permissions.includes(BillingPermissions.BILLING_CUSTOMER_WRITE)
              }}
              sx={{
                '&.overflow-hidden-ellipsis ': {
                  overflow: 'hidden !important',
                  whiteSpace: 'nowrap !important',
                  textOverflow: 'ellipsis !important'
                },
                '&.MuiDataGrid-root .MuiDataGrid-cell': {
                  overflow: 'hidden !important',
                  whiteSpace: 'nowrap !important',
                  textOverflow: 'ellipsis !important'
                },
                '.MuiDataGrid-cell': {
                  overflow: 'hidden !important',
                  whiteSpace: 'nowrap !important',
                  textOverflow: 'ellipsis !important'
                }
              }}
            />
          </Grid2>
        </Grid2>
      </Stack>
    </>
  );
};
